// src/pages/dashboard/SupportPage.tsx
import { useState, type FormEvent } from 'react';
import { brand } from '@/lib/tokens';
import { submitSupportTicket } from '@/lib/services/auth';
import { useToast } from '@/context/ToastContext';

const ISSUE_TYPES = [
  'Bug — something is broken',
  'Question — how do I…',
  'Feature request',
  'Billing / account',
  'Other',
];

export default function SupportPage() {
  const toast = useToast();
  const [issue, setIssue] = useState(ISSUE_TYPES[0]);
  const [message, setMessage] = useState('');
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!message.trim() || submitting) return;

    setSubmitting(true);
    try {
      await submitSupportTicket(issue, message.trim());
      setSent(true);
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : 'Could not send your ticket — please try again.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex-1 flex min-w-0" style={{ padding: '40px 56px' }}>
      <div style={{ maxWidth: 580, width: '100%' }}>
        <header className="mb-8">
          <span className="mono-label">Help &amp; Support</span>
          <h1
            className="font-serif mt-1.5"
            style={{ fontSize: 28, color: 'var(--text-primary)', letterSpacing: '-0.005em' }}
          >
            Raise a support ticket
          </h1>
        </header>

        {sent ? (
          <div className="glass-card flex items-center gap-3" style={{ padding: 24 }}>
            <span className="led led-green" />
            <p className="font-sans" style={{ color: 'var(--text-primary)', fontSize: 13.5 }}>
              Ticket sent — Timothy will reply to your account email directly.
            </p>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-7">
            <label className="block">
              <span className="mono-label" style={{ display: 'block', marginBottom: 6 }}>
                Issue type
              </span>
              <select
                value={issue}
                onChange={(e) => setIssue(e.target.value)}
                className="underline-input"
              >
                {ISSUE_TYPES.map((t) => (
                  <option key={t} value={t} style={{ background: 'var(--surface-2)' }}>
                    {t}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="mono-label" style={{ display: 'block', marginBottom: 6 }}>
                Message
              </span>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={7}
                className="underline-input font-serif"
                placeholder="Describe what you ran into — booking ID and timestamp help us debug fast."
                style={{ resize: 'none' }}
              />
            </label>

            <button
              type="submit"
              className="btn-pill"
              disabled={!message.trim() || submitting}
              style={{
                opacity: message.trim() && !submitting ? 1 : 0.4,
                cursor: message.trim() && !submitting ? 'pointer' : 'not-allowed',
              }}
            >
              {submitting ? 'Sending…' : 'Submit ticket'}
            </button>
          </form>
        )}

        <footer className="mt-12 pt-6" style={{ borderTop: '1px solid var(--hairline)' }}>
          <div
            className="flex items-center gap-2 mono-label"
            style={{ color: brand.textFaint }}
          >
            <span className="led led-green" />
            Support online · Avg. response 2hr
          </div>
        </footer>
      </div>
    </div>
  );
}
